import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '明星动态', to: '/stars' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword('');
    setMobileOpen(false);
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur transition-all duration-300 ${
        scrolled ? 'border-b border-background-200' : 'border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-moon-clear-fill text-lg" />
            </span>
            <span className="font-heading text-xl font-bold tracking-wide text-foreground-950 whitespace-nowrap">
              无颜的月
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(item.to)
                    ? 'text-primary-600'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden lg:flex items-center">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视作品、明星…"
                  className="h-9 w-56 rounded-full border border-background-300 bg-background-100 pl-9 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </form>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="打开导航菜单"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 md:hidden"
            >
              <i className={mobileOpen ? 'ri-close-line text-lg' : 'ri-menu-line text-lg'} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="space-y-1 px-4 py-3">
            <form onSubmit={handleSearch} className="mb-2">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视作品、明星…"
                  className="h-10 w-full rounded-full border border-background-300 bg-background-100 pl-9 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
            </form>
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`block rounded-md px-3 py-2 text-sm font-medium ${
                  isActive(item.to) ? 'bg-primary-50 text-primary-600' : 'text-foreground-700'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}